import { PlatformRedirectResponse } from '@quadminds/client-apis/auth';
import { CheckInviteResponse } from '@quadminds/client-apis/users';
import { ApplicationsEnum, CORE_PLATFORMS } from '@quadminds/shared/commons';
import { isDevelopment } from '@quadminds/shared/utils';
import axios, { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';

import { MutableRefObject } from 'react';
import { PLATFORM_URL } from '../config';
import { authApi } from '../services/api';
import { AuthApiError } from '../services/auth/auth-api-error';

const DEFAULT_URL = 'http://local.quadminds.com';

export type ValidatedInvite = {
    valid: boolean;
    inviteMatch: boolean;
    orgMatch: boolean;
};
export type PageErrorState = {
    state: {
        title: string;
        body: string;
        img: string;
        renderCta?: boolean;
    };
};
export type ErrorPathType = [string, PageErrorState];

export const validateInvite = (invite: CheckInviteResponse | undefined, queryParamOrgKey: string): ValidatedInvite => {
    const inviteMatch = Boolean(invite) && !(invite instanceof Error);
    const orgMatch = invite?.organization.id === queryParamOrgKey;
    const valid = invite?.expiresAt ? new Date(invite?.expiresAt).getTime() > Date.now() : false;
    return { inviteMatch, orgMatch, valid }; // Keys order matter for error handling
};

export const getCookie = (cookieName: string) => {
    const match = document.cookie.match(RegExp(String.raw`(?:^|;\s*)` + cookieName + '=([^;]*)'));
    return match ? match[1] : null;
};

// Handles session and returns url to go
// If browser has a session cookie just returns arguments initial redirectUrl
// Otherwise makes request to platform so session can be initialize and get users usual page
export const handlePlatformSession = async (redirectUrl: string = DEFAULT_URL) => {
    try {
        let authenticatedUrl = '';

        const res = await axios.get<PlatformRedirectResponse>(`${redirectUrl}/login`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            withCredentials: true,
        });

        if (res) {
            if (res.data.success && res.data.nextPage) {
                authenticatedUrl = res.data.nextPage;
            } else if (res.data.error && res.data.nextPage) {
                console.error(res.data);
                authenticatedUrl = res.data.nextPage;
            }
        }
        return authenticatedUrl;
    } catch (error) {
        // For Mindy, if legacy platform session fails but token is present, proceed with redirect
        const token = localStorage.getItem('token');
        if (token && isMindy) {
            return redirectUrl;
        }
        console.log('Error while getting platform url', error);
        return '';
    }
};

export const checkSession = async () => {
    try {
        const isSessionValid = Boolean((await authApi.checkSession()).accessToken);
        return isSessionValid;
    } catch (error) {
        console.error('Error checking session', error);
        return false;
    }
};

/**
 * Handles legacy platform session before redirect
 * Returns the url to go according to platform
 * @param {string} platformUrl - The URL of the platform.
 * @param {MutableRefObject<boolean>} hadPreviousValidToken - Indicates if there was a previous valid token.
 * @return {Promise<string | undefined>} The platform URL after handling the redirect.
 */
export const handleLegacyPlatformRedirect = async (
    platformUrl: string,
    hadPreviousValidToken: MutableRefObject<boolean>,
) => {
    // Check if platform session has already been set
    if (hadPreviousValidToken.current) {
        const hasCurrentSession = await checkSession();
        // Check if session is closed or expired
        if (!hasCurrentSession) {
            hadPreviousValidToken.current = false;
            localStorage.removeItem('token');
            return undefined;
        }
        return platformUrl;
    }

    // Inits platform session and get user's usual page
    platformUrl = await handlePlatformSession(platformUrl);

    return platformUrl;
};

export enum FIREBASE_AUTH_ERRORS {
    TOKEN_EXPIRED = 'auth/id-token-expired',
    USER_NOT_FOUND = 'auth/user-not-found',
    INTERNAL_ERROR = 'auth/internal-error',
    NETWORK_REQUEST_FAILED = 'auth/network-request-failed',
    TOO_MANY_ATTEMPTS_TRY_LATER = 'auth/too-many-requests',
    WRONG_PASS = 'auth/wrong-password',
    EMAIL_ALREADY_EXISTS = 'auth/email-already-exists',
    EMAIL_ALREADY_IN_USE = 'auth/email-already-in-use',
    MISSING_EMAIL = 'auth/missing-email',
    INVALID_EMAIL = 'auth/invalid-email',
    EMAIL_NOT_FOUND = 'auth/email-not-found',
    POPUP_CLOSED_BY_USER = 'auth/popup-closed-by-user',
    POPUP_BLOCKED = 'auth/popup-blocked',
    CANCELLED_POPUP_REQUEST = 'auth/cancelled-popup-request',
    ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL = 'auth/account-exists-with-different-credential',
}

export enum AUTH_API_ERRORS {
    USERS_API_NOT_FOUND = 'USERS_API_NOT_FOUND',
    USER_DISABLED_EXCEPTION = 'USER_DISABLED_EXCEPTION',
    ORGANIZATION_DISABLED_EXCEPTION = 'ORGANIZATION_DISABLED_EXCEPTION',
}

export type errorFirebaseOptionsType = { [key in FIREBASE_AUTH_ERRORS]?: ErrorPathType };

const builtErrorPath = (title: string, body: string, img = 'error_user', renderCta = true): ErrorPathType => [
    '/error',
    { state: { title, body, img, renderCta } },
];

export const handleFirebaseError = (error: FirebaseError): ErrorPathType | undefined => {
    const errorCases: errorFirebaseOptionsType = {
        // Login errors
        [FIREBASE_AUTH_ERRORS.TOKEN_EXPIRED]: builtErrorPath('invite.errors.valid.title', 'invite.errors.valid.body'),
        [FIREBASE_AUTH_ERRORS.TOO_MANY_ATTEMPTS_TRY_LATER]: builtErrorPath(
            'login.error.tooManyRequest.title',
            'login.error.tooManyRequest.body',
        ),
        [FIREBASE_AUTH_ERRORS.USER_NOT_FOUND]: builtErrorPath(
            'login.error.userNotFound.title',
            'login.error.userNotFound.body',
        ),
        [FIREBASE_AUTH_ERRORS.INTERNAL_ERROR]: builtErrorPath(
            'login.error.genericError.title',
            'login.error.genericError.body',
        ),
        // Signup errors
        [FIREBASE_AUTH_ERRORS.EMAIL_ALREADY_EXISTS]: builtErrorPath(
            'signup.error.emailAlreadyExists.title',
            'signup.error.emailAlreadyExists.body',
        ),
        [FIREBASE_AUTH_ERRORS.EMAIL_ALREADY_IN_USE]: builtErrorPath(
            'signup.error.emailAlreadyExists.title',
            'signup.error.emailAlreadyExists.body',
        ),
        [FIREBASE_AUTH_ERRORS.MISSING_EMAIL]: builtErrorPath(
            'signup.error.missingEmail.title',
            'signup.error.missingEmail.body',
        ),
        [FIREBASE_AUTH_ERRORS.EMAIL_NOT_FOUND]: builtErrorPath(
            'login.error.emailNotFound.title',
            'login.error.emailNotFound.body',
            'error_user',
            true,
        ),
    };

    return Object.keys(errorCases).includes(error.code) ? errorCases[error.code as FIREBASE_AUTH_ERRORS] : undefined;
};

export const handleAuthApiError = (error: AuthApiError): ErrorPathType | undefined => {
    const errorCases: Record<AUTH_API_ERRORS, ErrorPathType> = {
        [AUTH_API_ERRORS.USERS_API_NOT_FOUND]: builtErrorPath(
            'login.error.userNotFound.title',
            'login.error.userNotFound.body',
        ),
        [AUTH_API_ERRORS.USER_DISABLED_EXCEPTION]: builtErrorPath('login.error.userDisabled.title', error.message),
        [AUTH_API_ERRORS.ORGANIZATION_DISABLED_EXCEPTION]: builtErrorPath(
            'login.error.organizationDisabled.title',
            'login.error.organizationDisabled.body',
        ),
    };

    return Object.keys(errorCases).includes(error.code) ? errorCases[error.code as AUTH_API_ERRORS] : undefined;
};

export const shouldBypass = (byPassSearchParam: string | null) => {
    let bypass = false;
    if (isDevelopment()) {
        bypass = Boolean(byPassSearchParam);
    }
    return bypass;
};

export const IsAxiosUnAuthorized = (error: AxiosError): boolean => error?.response?.status === 401;

export const IsAxiosForbidden = (error: AxiosError): boolean => error?.response?.status === 403;

export const IsAxiosNotFound = (error: AxiosError): boolean => error?.response?.status === 404;

export const IsAxiosUnprocessableEntity = (error: AxiosError): boolean => error?.response?.status === 422;

export const IsAxiosServiceUnavailable = (error: AxiosError): boolean => error?.response?.status === 503;

export const IsAxiosInternalServerError = (error: AxiosError): boolean => error?.response?.status === 500;

export const IsMissingInAuthSystem = (error: AxiosError<{ code: string }>): boolean =>
    error.response?.data?.code === 'USER_MISSING_IN_AUTH_SYSTEM';

export enum FetchErrorsBigType {
    AXIOS_ERROR = 'axiosError',
    FIREBASE_ERROR = 'firebaseError',
    UNKNOWN_ERROR = 'unknownError',
}

export class LegacyLoginError extends Error {
    readonly code: string;

    constructor(code: string, message: string) {
        super(message);
        this.code = code;
        this.message = message;
    }
}

export enum SaasLegacyLoginErrorCodes {
    NO_APPS_ASSIGNED = '-3',
    SUSPENDED = '-4',
    DELETED = '-5',
    BLOCKED = '-6',
    DISALLOWED_CONCURRENCE = '-7',
    INVALID_CREDENTIALS = '-8',
}

export const typeOfError = (error: unknown): FetchErrorsBigType => {
    if (axios.isAxiosError(error)) {
        return FetchErrorsBigType.AXIOS_ERROR;
    } else if (error instanceof FirebaseError) {
        return FetchErrorsBigType.FIREBASE_ERROR;
    } else {
        return FetchErrorsBigType.UNKNOWN_ERROR;
    }
};

export function isBrowserEnvironment(): boolean {
    return typeof window !== 'undefined';
}

function urlContainsFleetcarePath(url: string | null | undefined): boolean {
    return typeof url === 'string' && url.includes('/apps/fleetcare');
}

function currentUrlTargetsFleetcare(): boolean {
    if (!isBrowserEnvironment()) return false;

    const { href, pathname, search } = window.location;
    if (urlContainsFleetcarePath(pathname) || urlContainsFleetcarePath(href)) return true;

    const params = new URLSearchParams(search);
    const redirect = params.get('redirect');
    const application = params.get('application');

    return urlContainsFleetcarePath(redirect) || application === 'fleetcare';
}

export const isStork = PLATFORM_URL?.includes(ApplicationsEnum.STORK) || PLATFORM_URL?.includes('tdc');

export const isSaas = PLATFORM_URL?.includes(ApplicationsEnum.SAAS) || PLATFORM_URL?.includes('https://test.quadminds'); // Para evitar que haga match con stork-test

export const isFlash = PLATFORM_URL?.includes(ApplicationsEnum.FLASH);

export const isMindy = PLATFORM_URL?.includes(ApplicationsEnum.MINDY) || PLATFORM_URL?.includes('mindy');

export const isOms = PLATFORM_URL?.includes(ApplicationsEnum.OMS) || PLATFORM_URL?.includes('tms');

export const isFleetcare = currentUrlTargetsFleetcare();

export const isCorePlatform = CORE_PLATFORMS.some((p) => PLATFORM_URL?.includes(p));

export const isStorkUser = (): boolean => localStorage.getItem('isStorkUser') === 'true';

// Platforms sharing the "branded" (dark, primary based) look & feel
export const isBrandedPlatform = isMindy || isFleetcare || isOms;

// Platforms whose users come from SaaS, so the login field may hold the user's code
// (such as 'nsa@operaciones') instead of an email: no email format validation allowed.
export const acceptsSaasCode = Boolean(isSaas || isMindy || isOms);

type PlatformVariants<T> = { branded: T; stork: T; fallback: T };

/**
 * Picks a value according to the running platform.
 * Keeps call sites free of nested ternaries.
 */
export function pickPlatformValue<T>({ branded, stork, fallback }: PlatformVariants<T>): T {
    if (isBrandedPlatform) return branded;
    if (isStork) return stork;
    return fallback;
}

// Claro co-branding is enabled through any query param key or value containing "claro"
export const hasClaroParam = (searchParams: URLSearchParams): boolean =>
    Array.from(searchParams.values()).some((v) => v.toLowerCase().includes('claro')) ||
    Array.from(searchParams.keys()).some((k) => k.toLowerCase().includes('claro'));

const FAVICON_BY_PLATFORM = (): string => {
    switch (true) {
        case isMindy:
            return './favicon_mindy.ico';
        case isFleetcare:
            return './favicon_fleetcare.ico';
        case isOms:
            return './favicon_oms.ico';
        case isStork:
            return './favicon_stork.ico';
        default:
            return './favicon.ico';
    }
};

// Sets the document favicon according to the running platform
export const applyBrandFavicon = (): void => {
    const link = document.querySelector<HTMLLinkElement>('[rel="icon"]');
    if (link) {
        link.href = FAVICON_BY_PLATFORM();
    }
};

/**
 * It differs from an standard email regex in the way that threats some strings:
 * It requires a TLD (.com, .net, etc)
 * This is required to prevent saas subusers codes as emails (soprole@antofagasta)
 * */
export function isEmail(str: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(str);
}
